/**
 * Handing the report to the browser.
 *
 * Kept apart from the report itself so the generator stays a pure function; all
 * the DOM lives here.
 */

/** Saves a string as a file the reader can keep. */
export const downloadFile = (contents, filename, type = "text/html;charset=utf-8") => {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked on a later tick: Safari cancels the download if the URL dies while
  // it is still being read.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

/**
 * PDF by way of the browser's own print pipeline.
 *
 * Chosen over a client-side PDF library on purpose. html2canvas-style libraries
 * rasterise the page: the text stops being text, so nothing in a 27-page report
 * can be searched, selected or read by a screen reader, and the file balloons.
 * Printing keeps it vector with real text, needs no dependency, and honours the
 * @page rules the report carries — the table head repeats on every page and
 * rows do not split.
 *
 * The cost is one dialog, where the reader picks "Save as PDF". The dialog's
 * default filename comes from the document title, which is why the report
 * titles itself the way it does.
 *
 * A hidden iframe rather than a new window: popup blockers stop window.open on
 * anything they do not tie to a click, and this leaves the dashboard exactly as
 * it was.
 */
export const printHtml = (html, { onDone } = {}) => {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  document.body.appendChild(frame);

  let finished = false;
  const cleanup = () => {
    if (finished) return;
    finished = true;
    if (frame.parentNode) frame.parentNode.removeChild(frame);
    onDone?.();
  };

  frame.onload = () => {
    const view = frame.contentWindow;
    if (!view) {
      cleanup();
      return;
    }
    // afterprint does not fire everywhere, so the timeout is the backstop that
    // stops a hidden iframe leaking into the page for the rest of the session.
    view.addEventListener?.("afterprint", cleanup);
    try {
      view.focus();
      view.print();
    } catch {
      cleanup();
      return;
    }
    setTimeout(cleanup, 60000);
  };

  frame.srcdoc = html;
};
